import { useCallback } from 'react';
import { useClient } from 'urql';

import fetchQuery from '@/helper/data-op/fetchQuery';
import useDefaultErrorSnackbar from '@/hooks/useDefaultErrorSnackbar';

import { CategoryFileFragment } from '../graphql/fragment/generated/CategoryFile';
import { CategoriesDocument, CategoriesQuery } from '../graphql/queries/generated/categories';
import { filterSubworkspaceCategories } from '../utils';
import { UseCategoriesProps } from './useCategories';

export default function useSubWorkspaceCategories() {
  const client = useClient();
  const { showDefaultErrorSnackbar } = useDefaultErrorSnackbar();

  const fetchCategories = useCallback(
    async (filters: UseCategoriesProps) => {
      const { error, data } = await fetchQuery(
        client,
        CategoriesDocument,
        {
          ...filters,
        },
        {},
      );

      if (error) {
        showDefaultErrorSnackbar();
        return { error };
      }

      const categories = (data as CategoriesQuery).categories?.edges?.map(({ node }) => node) as
        | CategoryFileFragment[]
        | undefined;

      return { categories };
    },
    [client, showDefaultErrorSnackbar],
  );

  const getCategoriesOnSubWorkspaceChange = useCallback(
    async (categoryIds?: string[] | null, subWorkspaceIds?: string[] | null) => {
      if (!categoryIds?.length) {
        return { categoryIds };
      }

      if (!subWorkspaceIds?.length) {
        return { categoryIds };
      }

      const { categories, error } = await fetchCategories({
        crossSubWorkspace: true,
        subWorkspaceIds: subWorkspaceIds,
      });

      if (error) {
        return { error };
      }

      const filteredCategories = filterSubworkspaceCategories({
        categoryIds,
        subWorkspaceIds,
        categories,
      });

      return { categoryIds: filteredCategories };
    },
    [fetchCategories],
  );

  return {
    getCategoriesOnSubWorkspaceChange,
  };
}
